/**
 * Geo reference: airports whose IATA code was officially CHANGED, keyed by the new code
 * and naming the code it replaced.
 *
 * Why this exists (#4267): West Palm Beach went PBI → DJT, and the sources we read do
 * not switch together — the dataset behind the generated tables moved on 2026-10-01,
 * the place catalog (voll-places) still lists only PBI, the legacy already emits vouchers
 * with DJT, and Cirium keeps `fs: PBI` with `iata: DJT`. A traveler types whichever code
 * the ticket shows. Each code must therefore resolve as the other while the sources
 * disagree, whatever snapshot each one is on.
 *
 * A short curated list of exceptions, not a synonym catalog: only official re-codings
 * of the same airport. Metro codes (SAO, LON) are `metro_city.ts`; popular names
 * ("Guarulhos") are `airport_names.ts`.
 *
 * Pure (no IO): compile-time table lookups.
 */
const RENAMED_FROM: Readonly<Record<string, string>> = {
  DJT: 'PBI', // West Palm Beach (FL) — dataset re-coded 2026-10-01
};

const RENAMED_TO: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(RENAMED_FROM).map(([current, former]) => [former, current]),
);

/**
 * Every code the airport answers to, the asked code first (`"DJT"` → `["DJT", "PBI"]`,
 * `"PBI"` → `["PBI", "DJT"]`); an airport that was never re-coded yields only itself.
 */
export function airportCodeAliases(code: string): string[] {
  const iata = code.trim().toUpperCase();
  const other = RENAMED_FROM[iata] ?? RENAMED_TO[iata];
  return other ? [iata, other] : [iata];
}

/** The first hit of `lookup` over the airport's codes — the asked code wins. */
export function lookupByAirportCode<T>(
  code: string,
  lookup: (iata: string) => T | null | undefined,
): T | null {
  for (const iata of airportCodeAliases(code)) {
    const hit = lookup(iata);
    if (hit !== null && hit !== undefined) return hit;
  }
  return null;
}
